import React, { useState, useEffect, useCallback } from "react";
import { customFetch } from '../apiClient';
import * as XLSX from "xlsx"; 

import defaultLogo from "../assests/kamlaLogo.png"; 

const ExpiryReports = () => {
  const [activeTab, setActiveTab] = useState("near-expiry");
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // Filters for History
  const [filters, setFilters] = useState({
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0]
  });

  // ✅ Pagination States Added
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // ✅ Summary State Added
  const [summary, setSummary] = useState({ totalLoss: 0, totalItems: 0, totalQty: 0 });

  const handleFilterChange = (e) => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));

  // ✅ Fetch Company Data (For Print Header)
  useEffect(() => {
    const fetchCompanyInfo = async () => {
  try {
        const res = await customFetch(`/PharmaCompany`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.length > 0) setCompanyInfo(data[0]);
        }
      } catch (err) { console.error("Failed to fetch company info", err); }
    };
    fetchCompanyInfo();
  }, []);

  const fetchReport = async () => {
    setLoading(true); setError(""); setReportData([]); setCurrentPage(1);
   try {
      const params = new URLSearchParams({
         fromDate: filters.fromDate,
         toDate: filters.toDate
      });

      let endpoint = "";
      if (activeTab === "near-expiry") endpoint = "near-expiry"; 
      else if (activeTab === "expired-stock") endpoint = "expired-stock";
      else if (activeTab === "damage-stock") endpoint = "damage-stock";

      // Yahan se API_BASE hataya gaya hai
      const url = activeTab === "near-expiry" 
          ? `/ExpiryReports/${endpoint}`
          : `/ExpiryReports/${endpoint}?${params.toString()}`;

      // Headers hata diye gaye hain
      const res = await customFetch(url);

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setReportData(data);

      // ✅ Calculate Summary for Cards
      if (activeTab === "near-expiry") {
         const estLoss = data.reduce((s, i) => s + (i.potentialLoss || i.PotentialLoss || 0), 0);
         setSummary({ totalItems: data.length, totalLoss: estLoss, totalQty: 0 });
      } else {
         const tQty = data.reduce((s, i) => s + (i.qtyPieces || i.QtyPieces || 0), 0);
         setSummary({ totalItems: data.length, totalLoss: 0, totalQty: tQty });
      }

    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); }, [activeTab]);

  // ✅ Pagination Logic
  const totalPages = Math.ceil(reportData.length / rowsPerPage) || 1;
  const displayedData = reportData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  // ==============================================================
  // ✅ EXCEL EXPORT
  // ==============================================================
  const exportToExcel = useCallback(() => {
    if (reportData.length === 0) { alert("No data available to export!"); return; }

    let formattedData = [];
    if (activeTab === 'near-expiry') {
      formattedData = reportData.map((item, i) => ({
        "S.No": i + 1, "Medicine": item.medicine || item.Medicine, "Brand": item.brandName || item.BrandName, 
        "Batch": item.batchNo || item.BatchNo, "Exp Date": formatDate(item.expDate || item.ExpDate),
        "Days Left": item.daysRemaining || item.DaysRemaining, "Stock (Pcs)": item.balancePieces || item.BalancePieces,
        "Stock (Box)": ((item.balancePieces || item.BalancePieces) / (item.packSize || item.PackSize || 1)).toFixed(2),
        "Est. Loss (₹)": item.potentialLoss || item.PotentialLoss
      }));
    } else if (activeTab === 'expired-stock' || activeTab === 'damage-stock') {
      formattedData = reportData.map((item, i) => ({
        "S.No": i + 1, "Date": formatDate(item.entryDate || item.EntryDate), "Rec ID": item.recId || item.RecId,
        "Medicine": item.medicine || item.Medicine, "Batch": item.batchNo || item.BatchNo, 
        "Exp Date": activeTab === 'expired-stock' ? formatDate(item.expDate || item.ExpDate) : "-",
        "Qty (Pcs)": item.qtyPieces || item.QtyPieces, "User": item.entryBy || item.EntryBy
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, `${activeTab.toUpperCase()}_REPORT.xlsx`);
  }, [reportData, activeTab]);

  // ==============================================================
  // ✅ TALLY / MARG STYLE PRINT & PDF
  // ==============================================================
  const handlePrint = useCallback(() => {
    if (reportData.length === 0) { alert("No data available to print!"); return; }

    const logoSource = companyInfo?.logoBase64 ? `data:image/png;base64,${companyInfo.logoBase64}` : defaultLogo;
    const compName = companyInfo?.company_Name || "HOSPITAL / PHARMACY STORE";
    const compAddress = companyInfo?.address || "";
    const compContact = companyInfo?.contact1 ? `Ph: ${companyInfo.contact1}` : "";
    const compGst = companyInfo?.reg_No ? `DL/GST: ${companyInfo.reg_No}` : "";

    const ROWS_PER_PAGE = 40; 
    const pages = [];
    let runningTotal = 0;

    for (let i = 0; i < reportData.length; i += ROWS_PER_PAGE) {
      const pageItems = reportData.slice(i, i + ROWS_PER_PAGE);
      const broughtForward = runningTotal;
      
      const pageTotal = pageItems.reduce((sum, item) => {
          if (activeTab === 'near-expiry') return sum + (item.potentialLoss || item.PotentialLoss || 0);
          return sum + (item.qtyPieces || item.QtyPieces || 0);
      }, 0);

      runningTotal += pageTotal;
      
      pages.push({
        items: pageItems,
        startIndex: i,
        broughtForward: broughtForward,
        carriedForward: runningTotal,
        isLastPage: i + ROWS_PER_PAGE >= reportData.length
      });
    }

    const reportHtml = pages.map((page, pageIndex) => {
      
      let thHtml = "";
      if (activeTab === 'near-expiry') thHtml = `<th style="text-align:left">Medicine</th><th style="text-align:left">Batch</th><th style="text-align:center">Exp Date</th><th style="text-align:center">Days Left</th><th style="text-align:right">Stock (Pcs)</th><th style="text-align:right">Est. Loss Value</th>`;
      else if (activeTab === 'expired-stock') thHtml = `<th style="text-align:left">Date</th><th style="text-align:left">Rec ID</th><th style="text-align:left">Medicine</th><th style="text-align:left">Batch</th><th style="text-align:center">Exp Date</th><th style="text-align:right">Qty (Pcs)</th><th style="text-align:left">User</th>`;
      else if (activeTab === 'damage-stock') thHtml = `<th style="text-align:left">Date</th><th style="text-align:left">Rec ID</th><th style="text-align:left">Medicine</th><th style="text-align:left">Batch</th><th style="text-align:right">Qty (Pcs)</th><th style="text-align:left">User</th>`;

      const trHtml = page.items.map((item, idx) => {
        if (activeTab === 'near-expiry') return `<tr><td style="text-align:left"><div><small style="color:#555">${item.brandName || item.BrandName || '-'}</small></div><strong>${item.medicine || item.Medicine}</strong></td><td style="text-align:left">${item.batchNo || item.BatchNo}</td><td style="text-align:center; font-weight:bold; color:#dc2626;">${formatDate(item.expDate || item.ExpDate)}</td><td style="text-align:center">${item.daysRemaining || item.DaysRemaining}</td><td style="text-align:right">${item.balancePieces || item.BalancePieces}</td><td style="text-align:right; font-weight:bold;">${(item.potentialLoss || item.PotentialLoss || 0)?.toFixed(2)}</td></tr>`;
        if (activeTab === 'expired-stock') return `<tr><td style="text-align:left">${formatDate(item.entryDate || item.EntryDate)}</td><td style="text-align:left">${item.recId || item.RecId}</td><td style="text-align:left"><strong>${item.medicine || item.Medicine}</strong></td><td style="text-align:left">${item.batchNo || item.BatchNo || '-'}</td><td style="text-align:center; color:#dc2626;">${formatDate(item.expDate || item.ExpDate)}</td><td style="text-align:right; font-weight:bold;">${item.qtyPieces || item.QtyPieces}</td><td style="text-align:left">${item.entryBy || item.EntryBy}</td></tr>`;
        if (activeTab === 'damage-stock') return `<tr><td style="text-align:left">${formatDate(item.entryDate || item.EntryDate)}</td><td style="text-align:left">${item.recId || item.RecId}</td><td style="text-align:left"><strong>${item.medicine || item.Medicine}</strong></td><td style="text-align:left">${item.batchNo || item.BatchNo || '-'}</td><td style="text-align:right; font-weight:bold; color:#dc2626;">${item.qtyPieces || item.QtyPieces}</td><td style="text-align:left">${item.entryBy || item.EntryBy}</td></tr>`;
      }).join('');

      let colSpanBF = '5';
      if(activeTab === 'expired-stock') colSpanBF = '5';
      if(activeTab === 'damage-stock') colSpanBF = '4';

      const totalLabel = activeTab === 'near-expiry' ? "Est. Loss Value (₹):" : "Total Qty (Pcs):";

      const bfHtml = pageIndex > 0 ? `
        <tr style="background:#f9f9f9; border-bottom: 1px solid #000;">
          <td colspan="${colSpanBF}" style="text-align:right; font-weight:bold; font-style:italic;">Brought Forward (B/F):</td>
          <td style="text-align:right; font-weight:bold;">${page.broughtForward.toFixed(2)}</td>
          ${activeTab !== 'near-expiry' ? '<td></td>' : ''}
        </tr>` : "";

      const cfHtml = `
        <tr style="background:#e0e0e0;">
          <td colspan="${colSpanBF}" style="text-align:right; font-weight:bold; font-size:12px; padding: 6px;">
             ${page.isLastPage ? `GRAND TOTAL (${totalLabel}):` : 'Carried Forward (C/F):'}
          </td>
          <td style="text-align:right; font-weight:bold; font-size:12px; padding: 6px;">${page.carriedForward.toFixed(2)}</td>
          ${activeTab !== 'near-expiry' ? '<td></td>' : ''}
        </tr>`;

      return `
        <div class="a4-page ${page.isLastPage ? 'last-page' : ''}">
          <div class="marg-header">
            <img src="${logoSource}" alt="Logo" class="print-logo"/>
            <div class="marg-header-center">
              <h2>${compName}</h2>
              <p>${compAddress}</p>
              <p><strong>${compContact}</strong></p>
            </div>
            <div class="marg-header-right">
              ${compGst ? `<span><strong>${compGst}</strong></span><br/>` : ""}
              <span><strong>Page:</strong> ${pageIndex + 1} of ${pages.length}</span><br/>
              <span><strong>Print Date:</strong> ${new Date().toLocaleDateString('en-GB')}</span>
            </div>
          </div>
          
          <div class="marg-title-strip">
            ${activeTab.toUpperCase().replace('-', ' ')} REPORT
            ${activeTab !== 'near-expiry' ? `<br/>(FROM: ${formatDate(filters.fromDate)} TO: ${formatDate(filters.toDate)})` : ''}
          </div>

          <div class="content-area">
            <table class="items-table">
              <thead><tr>${thHtml}</tr></thead>
              <tbody>${bfHtml}${trHtml}</tbody>
            </table>
          </div>
          
          <div class="footer-area">
             <table class="items-table" style="margin:0; border-top: 2px solid #000;"><tbody>${cfHtml}</tbody></table>
          </div>
        </div>
      `;
    }).join("");

    const pw = window.open("", "_blank", "width=900,height=700");
    pw.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${activeTab.toUpperCase()} Report</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
          body { background: #525659; display: flex; flex-direction: column; align-items: center; padding: 20px; }
          @media print {
            @page { size: A4 portrait; margin: 8mm; }
            body { background: #fff; padding: 0; margin: 0;}
            .a4-page { width: 100% !important; min-height: 275mm; page-break-after: always; margin: 0 !important; border: 1px solid #000 !important; }
            .a4-page.last-page { page-break-after: auto; }
          }
          .a4-page { background: #fff; width: 210mm; min-height: 280mm; border: 2px solid #000; margin-bottom: 20mm; display: flex; flex-direction: column; box-shadow: 0 0 10px rgba(0,0,0,0.5); }
          .marg-header { border-bottom: 2px solid #000; padding: 10px 15px; display: flex; justify-content: space-between; align-items: center; }
          .print-logo { width: 75px; height: 75px; object-fit: contain; flex-shrink:0;}
          .marg-header-center { flex-grow: 1; padding: 0 15px; text-align:center;}
          .marg-header-center h2 { margin: 0 0 4px 0; font-size: 18px; text-transform: uppercase; }
          .marg-title-strip { background-color: #e0e0e0 !important; border-bottom: 2px solid #000; text-align: center; padding: 5px; font-weight: bold; font-size: 13px; -webkit-print-color-adjust: exact !important;}
          .content-area { flex-grow: 1; padding: 0; } 
          .footer-area { margin-top: auto; }
          .items-table { width: 100%; border-collapse: collapse; font-size: 11px; }
          .items-table th { border-bottom: 2px solid #000; border-right: 1px solid #ccc; background-color: #f1f1f1 !important; padding: 6px 8px; font-weight: bold; text-transform: uppercase; -webkit-print-color-adjust: exact !important;}
          .items-table td { border-right: 1px dashed #ccc; border-bottom: 1px dashed #ccc; padding: 5px 8px; }
          .items-table th:last-child, .items-table td:last-child { border-right: none; }
        </style>
      </head>
      <body>${reportHtml}</body>
      </html>
    `);
    pw.document.close();
    setTimeout(() => { pw.focus(); pw.print(); }, 500);
  }, [reportData, activeTab, filters, companyInfo]);

  // ✅ Listen to Export Events (From Sidebar/Header)
  useEffect(() => {
    const handleExportEvent = (e) => {
      const exportType = e.detail?.type;
      if (exportType === 'excel') exportToExcel();
      else if (exportType === 'pdf' || exportType === 'print') handlePrint(); 
    };
    window.addEventListener("exportData", handleExportEvent);
    return () => window.removeEventListener("exportData", handleExportEvent);
  }, [exportToExcel, handlePrint]);

  return (
    <div className="report-container">
      <div className="report-card">
        
        <div className="card-header">
          <div className="header-left">
             <div className="header-icon"><i className="bi bi-trash"></i></div>
             <div className="header-text"><h5>Expiry & Loss Reports</h5><p>Track Near Expiry, Expired & Damaged Stock</p></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-header">
           <button className={`tab-btn ${activeTab === 'near-expiry' ? 'active' : ''}`} onClick={() => setActiveTab('near-expiry')}>
              <i className="bi bi-hourglass-split"></i> Near Expiry Alert (6 Months)
           </button>
           <button className={`tab-btn ${activeTab === 'expired-stock' ? 'active' : ''}`} onClick={() => setActiveTab('expired-stock')}>
              <i className="bi bi-trash3"></i> Expired Stock History
           </button>
           <button className={`tab-btn ${activeTab === 'damage-stock' ? 'active' : ''}`} onClick={() => setActiveTab('damage-stock')}>
              <i className="bi bi-bandaid"></i> Damage / Breakage
           </button>
        </div>

        <div className="card-content">
          {error && <div className="alert-custom alert-danger">{error}</div>}

          {/* Filters Bar - Only for History Tabs */}
          {activeTab !== 'near-expiry' && (
              <div className="filter-bar">
                 <div className="form-group">
                    <label>From Date</label>
                    <input type="date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} className="form-input" />
                 </div>
                 <div className="form-group">
                    <label>To Date</label>
                    <input type="date" name="toDate" value={filters.toDate} onChange={handleFilterChange} className="form-input" />
                 </div>
                 <button className="btn-search" onClick={fetchReport} disabled={loading}>
                    {loading ? 'Wait...' : 'View History'}
                 </button>
              </div>
          )}

          {/* ✅ SUMMARY CARDS */}
          {!loading && reportData.length > 0 && (
             <div className="summary-cards">
                <div className="s-card total">
                   <div className="s-label">{activeTab === 'near-expiry' ? 'Medicines at Risk' : 'Total Records'}</div>
                   <div className="s-value">{summary.totalItems}</div>
                   <div className="s-sub">Batches Found</div>
                </div>
                
                {activeTab === 'near-expiry' ? (
                   <div className="s-card returns">
                      <div className="s-label">Total Estimated Loss</div>
                      <div className="s-value">₹ {summary.totalLoss.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   </div>
                ) : (
                   <div className="s-card cash">
                      <div className="s-label">Total Quantity Lost (Pcs)</div>
                      <div className="s-value">{summary.totalQty}</div>
                   </div>
                )}
             </div>
          )}

          {/* Tables */}
          <div className="table-section">
            <div className="table-responsive">
              <table className="custom-table">
                <thead>
                  {activeTab === 'near-expiry' && (
                     <tr>
                        <th className="text-start">Medicine</th><th className="text-start">Batch</th><th className="text-center">Exp Date</th>
                        <th className="text-center">Days Left</th>
                        <th className="text-end">Stock (Pcs)</th>
                        <th className="text-end">Stock (Box)</th>
                        <th className="text-end">Est. Loss Value</th>
                     </tr>
                  )}
                  {activeTab === 'expired-stock' && (
                     <tr>
                        <th className="text-start">Date</th><th className="text-start">Rec ID</th><th className="text-start">Medicine</th><th className="text-start">Batch</th>
                        <th className="text-center">Exp Date</th>
                        <th className="text-end">Qty (Pcs)</th>
                        <th className="text-start">User</th>
                     </tr>
                  )}
                  {activeTab === 'damage-stock' && (
                     <tr>
                        <th className="text-start">Date</th><th className="text-start">Rec ID</th><th className="text-start">Medicine</th><th className="text-start">Batch</th>
                        <th className="text-end">Qty (Pcs)</th>
                        <th className="text-start">User</th>
                     </tr>
                  )}
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="empty-state">Loading data...</td></tr>
                  ) : displayedData.length === 0 ? (
                    <tr><td colSpan="7" className="empty-state">No records found.</td></tr>
                  ) : (
                    displayedData.map((item, index) => {
                      const daysLeft = item.daysRemaining || item.DaysRemaining;
                      return (
                      <tr key={index}>
                        
                        {activeTab === 'near-expiry' && <>
                           <td className="text-start">
                              <div><small className="text-muted d-block" style={{fontSize:'10px', lineHeight:'1'}}>{item.brandName || item.BrandName || '-'}</small></div>
                              <span className="fw-bold">{item.medicine || item.Medicine}</span>
                           </td>
                           <td className="text-start">{item.batchNo || item.BatchNo}</td>
                           <td className="text-center fw-bold text-danger">{formatDate(item.expDate || item.ExpDate)}</td>
                           
                           <td className="text-center">
                                <span className={`badge-days ${daysLeft < 30 ? 'bg-critical' : 'bg-warning'}`}>
                                    {daysLeft} Days
                                </span>
                           </td>

                           <td className="text-end fw-medium">{item.balancePieces || item.BalancePieces}</td>
                           <td className="text-end text-muted">
                               {((item.balancePieces || item.BalancePieces) / (item.packSize || item.PackSize || 1)).toFixed(2)}
                           </td>
                           <td className="text-end text-danger fw-bold">₹{(item.potentialLoss || item.PotentialLoss || 0)?.toFixed(2)}</td>
                        </>}

                        {activeTab === 'expired-stock' && <>
                           <td className="text-start text-muted">{formatDate(item.entryDate || item.EntryDate)}</td>
                           <td className="text-start text-muted text-sm">{item.recId || item.RecId}</td>
                           <td className="text-start fw-bold">{item.medicine || item.Medicine}</td>
                           <td className="text-start">{item.batchNo || item.BatchNo || '-'}</td>
                           <td className="text-center text-danger">{formatDate(item.expDate || item.ExpDate)}</td>
                           <td className="text-end fw-bold">{item.qtyPieces || item.QtyPieces}</td>
                           <td className="text-start text-sm">{item.entryBy || item.EntryBy}</td>
                        </>}

                        {activeTab === 'damage-stock' && <>
                           <td className="text-start text-muted">{formatDate(item.entryDate || item.EntryDate)}</td>
                           <td className="text-start text-muted text-sm">{item.recId || item.RecId}</td>
                           <td className="text-start fw-bold">{item.medicine || item.Medicine}</td>
                           <td className="text-start">{item.batchNo || item.BatchNo || '-'}</td>
                           <td className="text-end fw-bold text-danger">{item.qtyPieces || item.QtyPieces}</td>
                           <td className="text-start text-sm">{item.entryBy || item.EntryBy}</td>
                        </>}

                      </tr>
                    )})
                  )}
                </tbody>
              </table>
            </div>

            {/* ✅ Pagination Controls */}
            {!loading && reportData.length > 0 && (
              <div className="d-flex justify-content-between align-items-center p-2 border-top bg-light">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{fontSize:'12px'}}>Rows per page:</span>
                  <select 
                    className="form-select form-select-sm" 
                    style={{width: '70px', fontSize: '12px', padding: '2px 10px'}}
                    value={rowsPerPage} 
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{fontSize:'12px', marginRight:'10px'}}>
                    Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, reportData.length)} of {reportData.length} entries
                  </span>
                  <button 
                    className="btn btn-sm btn-outline-secondary py-1 px-2" 
                    disabled={currentPage === 1} 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  ><i className="bi bi-chevron-left"></i> Prev</button>
                  <span className="fw-bold" style={{fontSize:'12px'}}> Page {currentPage} of {totalPages} </span>
                  <button 
                    className="btn btn-sm btn-outline-secondary py-1 px-2" 
                    disabled={currentPage === totalPages} 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  >Next <i className="bi bi-chevron-right"></i></button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      <style jsx>{`
        /* Consistent Theme CSS */
        .report-container { padding: 0; font-size: 12px; }
        .report-card { background: white; border-radius: 8px; box-shadow: 0 1px 10px rgba(0,0,0,0.05); border: 1px solid #e0e6ed; overflow: hidden; }
        .card-header { background: var(--theme-color, #0088cc); padding: 8px 15px; }
        .header-left { display: flex; align-items: center; gap: 10px; color: white; }
        .header-icon { width: 32px; height: 32px; background: rgba(255, 255, 255, 0.2); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
        .header-text h5 { margin: 0; font-size: 15px; font-weight: 700; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 11px; }

        .tabs-header { display: flex; background: #f8fafc; border-bottom: 1px solid #e0e6ed; padding: 0 15px; gap: 5px; overflow-x: auto; }
        .tab-btn { padding: 10px 14px; border: none; background: transparent; color: #64748b; font-weight: 600; font-size: 12px; cursor: pointer; border-bottom: 2px solid transparent; display: flex; gap: 6px; align-items: center; white-space: nowrap; }
        .tab-btn:hover { color: var(--theme-color, #0088cc); }
        .tab-btn.active { color: var(--theme-color, #0088cc); border-bottom-color: var(--theme-color, #0088cc); background: white; border-radius: 6px 6px 0 0; border: 1px solid #e0e6ed; border-bottom: none; position: relative; top: 1px; }

        .card-content { padding: 15px; }
        .filter-bar { display: flex; gap: 8px; background: #f8fafc; padding: 10px; border-radius: 6px; border: 1px solid #e0e6ed; margin-bottom: 12px; align-items: flex-end; flex-wrap: wrap; }
        .form-group { display: flex; flex-direction: column; flex: 1; min-width: 120px; max-width: 200px;}
        .form-group label { font-size: 11px; font-weight: 600; margin-bottom: 3px; color: #475569; }
        .form-input { padding: 4px 8px; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 12px; height: 30px; outline: none; }
        .btn-search { padding: 0 15px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-weight: 600; cursor: pointer; height: 30px; font-size: 12px; transition: 0.2s; }

        .summary-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin-bottom: 15px; }
        .s-card { padding: 12px; border-radius: 8px; color: white; position: relative; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .s-card.total { background: linear-gradient(135deg, #3b82f6, #2563eb); }
        .s-card.cash { background: linear-gradient(135deg, #10b981, #059669); }
        .s-card.returns { background: linear-gradient(135deg, #ef4444, #b91c1c); }
        .s-label { font-size: 11px; opacity: 0.9; margin-bottom: 3px; text-transform: uppercase; }
        .s-value { font-size: 18px; font-weight: 700; }
        .s-sub { font-size: 10px; opacity: 0.8; margin-top: 2px; }

        .table-section { background: white; border-radius: 6px; border: 1px solid #e0e6ed; overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .custom-table th { background: #f8fafc; color: #334155; padding: 8px 10px; font-weight: 600; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
        .custom-table td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; color: #1e293b; }
        .custom-table tr:hover { background-color: #f8fafc; }

        .text-start { text-align: left !important; }
        .text-center { text-align: center !important; }
        .text-end { text-align: right !important; }
        .fw-bold { font-weight: 700; }
        .fw-medium { font-weight: 500; }
        .text-success { color: #16a34a !important; }
        .text-danger { color: #dc2626 !important; }
        .text-primary { color: var(--theme-color, #0088cc) !important; }
        .text-muted { color: #64748b !important; }
        .empty-state { text-align: center; padding: 30px; color: #94a3b8; font-style: italic; }

        /* Badge Styles */
        .badge-days { padding: 4px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; color: white; display: inline-block;}
        .bg-critical { background: #dc2626; animation: pulse 2s infinite; }
        .bg-warning { background: #f59e0b; color: #000;}

        @keyframes pulse {
            0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); }
            70% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
            100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
        }
      `}</style>
    </div>
  );
};

export default ExpiryReports;